"use client";

import { useState } from "react";
import { track } from "@/lib/analytics";

/**
 * Priority-request capture for library templates that aren't in the builder
 * yet: email (so we can notify them) + optional job details. Stored as a
 * report_request page event in D1 with priority=true.
 */
export function PriorityRequest({ templateId, title, trade }: { templateId: string; title: string; trade: string }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [details, setDetails] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "done" | "error">("idle");

  const inputCls =
    "w-full rounded-lg border border-line bg-white px-3 py-2 text-[0.84rem] text-ink outline-none transition-colors placeholder:text-muted/60 focus:border-emerald focus:ring-2 focus:ring-emerald/15";

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.includes("@") || status === "loading") return;
    setStatus("loading");
    try {
      const res = await fetch("/api/event", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          source: window.location.hostname.replace(/^www\./, ""),
          session_id: crypto.randomUUID(),
          event_type: "report_request",
          page: window.location.pathname,
          data: JSON.stringify({
            template: templateId,
            title: title,
            trade: trade,
            email: email.trim().toLowerCase(),
            details: details.trim().substring(0, 400),
            priority: true,
          }),
        }),
      });
      if (!res.ok) throw new Error(String(res.status));
      track("report_priority_requested", { template: templateId, trade });
      setStatus("done");
    } catch {
      setStatus("error");
    }
  }

  if (status === "done") {
    return (
      <div className="rounded-xl border border-emerald/30 bg-emerald-tint p-4">
        <p className="text-sm font-semibold text-ink">Request received.</p>
        <p className="mt-1 text-sm text-body">We build the most-requested reports first — we’ll email you when this one is ready.</p>
      </div>
    );
  }

  return (
    <div>
      <button
        type="button"
        onClick={() => {
          setOpen((v) => !v);
          if (!open) track("report_priority_request_opened", { template: templateId, trade });
        }}
        className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-emerald px-5 py-3 text-[0.9rem] font-semibold text-white transition-colors hover:bg-emerald-deep"
      >
        Priority-request this report
      </button>
      {open && (
        <form onSubmit={submit} className="mt-3 space-y-2.5">
          <input
            type="email"
            required
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Work email — we’ll tell you when it’s ready"
            aria-label="Work email"
            className={inputCls}
          />
          <textarea
            rows={2}
            value={details}
            onChange={(e) => setDetails(e.target.value)}
            maxLength={400}
            placeholder="Anything specific about your job? (optional)"
            aria-label="Job details (optional)"
            className={inputCls}
          />
          <button
            type="submit"
            disabled={status === "loading" || !email.includes("@")}
            className="w-full rounded-full bg-emerald px-5 py-2.5 text-[0.86rem] font-semibold text-white transition-colors hover:bg-emerald-deep disabled:opacity-60"
          >
            {status === "loading" ? "Sending…" : "Send request"}
          </button>
          {status === "error" && <p role="alert" className="text-sm text-risk-veryhigh">Something went wrong — try again or email hello@ramsdocs.co.uk.</p>}
        </form>
      )}
    </div>
  );
}
